<template>
  <form class="topbar-search hide-for-print" :class="{ 'active': searchIsActive }" @submit.prevent="handleSearchSubmit">
    <div class="input-container search" :class="{ 'search-active': searchIsActive }">
      <input id="topbar-search-input" autocomplete="off" class="rounded" v-model="params.query" type="text" placeholder="Søk…" :disabled="this.isSearching || this.isUpdating" @focus="showSearch" :style="{ 'background-color': avantiEnv === 'staging' ? '#fff' : 'transparent' }">

      <div class="search-category-picker has-popover" :class="{'popover-active': searchTypePopoverActive}">
        <button type="button" @click="toggleSearchTypePopover()" class="search-category-picker-button"><span>{{selectedSearchType.name}}</span> <svg width="5px" height="10px" viewBox="0 0 5 10"><use xlink:href="/static/sprites.svg#glyph-input-select-small"></use></svg></button>
        <span class="popover radio align-x-center align-y-bottom large">
          <ul>
            <li v-for="typeItem in searchTypeOptions" v-bind:key="'type-' + typeItem.type" :class="{ 'active': selectedSearchType.type === typeItem.type }">
              <a href="javascript:;" @click="changeSearchType(typeItem)">
                <span class="radio-glyph"></span>
                <span class="radio-title">{{typeItem.name}}</span>
              </a>
            </li>
          </ul>
        </span>
      </div>

      <button class="glyph-button" type="submit">Søk <svg width="14px" height="14px" viewBox="0 0 14 14"><use xlink:href="/static/sprites.svg#glyph-input-search"></use></svg></button>
      <a href="javascript:;" class="cancel-search" @click="clearSearch"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-close"></use></svg></a>
    </div>

    <div class="modal-container">
      <div class="modal-window sidepanel left">
        <div class="content-container flex-row">
          <section class="content background">
            <div class="modal-window-header no-padding">
              <div class="topbar-search-placeholder"></div>
            </div>

            <!-- Normal Search -->
            <template v-if="searchMode !== 'advanced'">
              <TheMainSearchNormalIntro v-if="!searchIsPerformed"/>

              <div class="modal-window-body active" v-else-if="isSearching">
                <TheLoader :small="true" :active="true"/>
              </div>

              <TheMainSearchNormalResultEmpty v-else-if="!totalFound"/>
              <TheMainSearchNormalResult :result="result" :filter="activeFilter" v-else/>
            </template>

            <!-- Advanced Search -->
            <template v-else-if="searchMode === 'advanced'">
              <div class="modal-window-body large no-padding active">
                <TheMainSearchAdvancedForm :result="result" :params="params" :settings="userSettings" :defaultColumns="defaultColumns" :exportParams="exportParams" v-on:on_update_columns="onUpdateColumns"/>
                <TheMainSearchAdvancedIntro v-if="!searchIsPerformed"/>
                <TheMainSearchAdvancedResult v-else :result="result" :settings="userSettings"/>
                <br><br>
              </div>
            </template>
          </section>

          <!-- The Filter -->
          <div v-if="showFilterView" class="sidebar right fixed subnav loaded" id="topbar-search-results-filter">
            <div class="fixed-container">
              <ul>
                <li><a @click="toggleFilter(null)" :class="{'active': !activeFilter}" href="#">Alle <span class="count">{{totalFound}}</span></a></li>
                <li v-if="result.objekt.total"><a @click="toggleFilter('objekt')" :class="{'active': activeFilter === 'objekt'}" href="javascript:;">Objekter <span class="count">{{result.objekt.total}}</span></a></li>
                <li v-if="result.kunde.total"><a @click="toggleFilter('kunde')" :class="{'active': activeFilter === 'kunde'}" href="javascript:;">Kunder <span class="count">{{result.kunde.total}}</span></a></li>
                <li v-if="result.kunstner.total"><a @click="toggleFilter('kunstner')" :class="{'active': activeFilter === 'kunstner'}" href="javascript:;">Kunstner register <span class="count">{{result.kunstner.total}}</span></a></li>
                <li v-if="result.faktura.total"><a @click="toggleFilter('faktura')" :class="{'active': activeFilter === 'faktura'}" href="javascript:;">Fakturaer <span class="count">{{result.faktura.total}}</span></a></li>
                <li v-if="result.kontrakt.total"><a @click="toggleFilter('kontrakt')" :class="{'active': activeFilter === 'kontrakt'}" href="javascript:;">Kontrakter <span class="count">{{result.kontrakt.total}}</span></a></li>
              </ul>
            </div>
          </div>
          <!-- The Filter -->
        </div>
      </div>

      <div class="modal-overlay" @click="hideSearch"></div>
    </div>
  </form>
</template>

<script>
import moment from 'moment'
import bus from '@/plugins/event-bus'
import accounting from 'accounting-js'
import SearchService from '@/services/SearchService'
import TheMainSearchNormalIntro from './TheMainSearchNormalIntro'
import TheMainSearchNormalResult from './TheMainSearchNormalResult'
import TheMainSearchNormalResultEmpty from './TheMainSearchNormalResultEmpty'
import TheMainSearchAdvancedForm from './TheMainSearchAdvancedForm'
import TheMainSearchAdvancedIntro from './TheMainSearchAdvancedIntro'
import TheMainSearchAdvancedResult from './TheMainSearchAdvancedResult'

export default {
  name: 'TheMainSearch',
  components: {
    TheMainSearchNormalIntro,
    TheMainSearchNormalResult,
    TheMainSearchNormalResultEmpty,
    TheMainSearchAdvancedForm,
    TheMainSearchAdvancedIntro,
    TheMainSearchAdvancedResult
  },
  data () {
    return {
      result: {},
      exportParams: null,
      activeFilter: null,
      searchIsActive: false,
      searchIsPerformed: false,
      searchTypePopoverActive: false,
      searchTypeOptions: [
        { type: '', name: 'Søk i alt' },
        { type: 'objekt', name: 'Objekt' },
        { type: 'kunde', name: 'Kunde' },
        { type: 'kunstner', name: 'Kunstner register' },
        { type: 'faktura', name: 'Faktura' },
        { type: 'kontrakt', name: 'Kontrakt' }
      ],
      userSettings: {
        columns: [],
        listtype: 'small',
        filter: true
      },
      defaultColumns: [
        { name: 'Bilde', key: 'photos' },
        { name: 'Objektnr', key: '' },
        { name: 'Auksjonsdato', key: '' },
        { name: 'Kunstner', key: '' },
        { name: 'Tittel', key: '' },
        { name: 'Beskrivelse', key: '' },
        { name: 'Tekst', key: '' },
        { name: 'Tilslag', key: '' },
        { name: 'Vurdering', key: '' },
        { name: 'Minstepris', key: '' },
        { name: 'Signert', key: '' },
        { name: 'Lokasjon', key: 'lokasjon.Lokasjonkode' },
        { name: 'Hentet', key: 'Lev_dato' },
        { name: 'Avdeling', key: 'avdeling.Beskrivelse' },
        { name: 'Type', key: 'Type' },
        { name: 'Registrert dato', key: 'Registrert_dato' },
        { name: 'Relevans', key: 'Relevans' },
        { name: 'Tekst_ok', key: 'Tekst_ok' },
        { name: 'Fototatt', key: 'Fototatt' },
        { name: 'Bet_dato', key: 'Bet_dato' },
        { name: 'Lev_dato', key: 'Lev_dato' },
        { name: 'Fakturert', key: 'Fakturert' },
        { name: 'oppgjor_dato', key: 'oppgjor_dato' },
        { name: 'oppgjor_belop', key: 'oppgjor_belop' },
        { name: 'Katalognr', key: 'Katalognr' },
        { name: 'Kontraktansvarlig', key: 'kontraktsansvarlig.Brukernavn' }
      ],
      params: {},
      defaultSearchParams: {
        type: '', // Default search in everything
        date_from: null,
        date_to: null,
        price_from: null,
        price_to: null,
        Auksjon: null,
        AukjsonSearchAll: false,
        Kunstnername: null,
        Kunstnerid: null,
        Objekttype: null,
        Avdelingsid: null,
        Kategori: null,
        query: localStorage.searchQuery
      }
    }
  },
  created () {
    var vm = this

    this.params = this._.clone(this.defaultSearchParams)

    this.loadUserSettings()

    // See if we are coming from the new object search...
    let searchChanged = null
    try {
      searchChanged = sessionStorage.getItem('search-changed')
        ? JSON.parse(sessionStorage.getItem('search-changed'))
        : null
    } catch (e) {
      // Ignore
      console.log('Errror')
      console.log(e)
    }

    if (searchChanged) {
      // if coming form new obj search:
      // store the type and query
      // and reset the session storage
      // and run a search...
      this.params.type = searchChanged.t || ''
      this.params.query = searchChanged.q || ''

      sessionStorage.removeItem('search-changed')

      this.resetResult()
      this.handleSearchSubmit()
    } else if (localStorage.searchResultNew == null) {
      this.resetResult()
    } else {
      this.exportParams = localStorage.searchExport ? JSON.parse(localStorage.searchExport) : null

      this.result = JSON.parse(localStorage.searchResultNew)

      if (!this.result.faktura) {
        this.result.faktura = { page: 1, total: 0, pages: 0, items: [], searching: false, updating: false }
      }

      if (!this.result.kontrakt) {
        this.result.kontrakt = { page: 1, total: 0, pages: 0, items: [], searching: false, updating: false }
      }

      this.result.items = []
      this.arrangeObjektData()
        .then(data => {
          this.result.objekt.items = data
          this.searchIsPerformed = true

          this.result.objekt.updating = false
          this.result.objekt.searching = false

          this.result.kunde.updating = false
          this.result.kunde.searching = false

          this.result.kunstner.updating = false
          this.result.kunstner.searching = false

          this.result.faktura.updating = false
          this.result.faktura.searching = false

          this.result.kontrakt.updating = false
          this.result.kontrakt.searching = false
        })
    }

    // Click outside - close search type popover
    window.jQuery(document).mouseup(function (e) {
      let container = window.jQuery('.has-popover')

      // if the target of the click isn't the container nor a descendant of the container
      if (!container.is(e.target) && container.has(e.target).length === 0) {
        vm.searchTypePopoverActive = false
      }

      // Click somewhere no inside this component - or its descendants...
      let mainContainer = window.jQuery('.topbar-search')
      if (!mainContainer.is(e.target) && mainContainer.has(e.target).length === 0) {
        // console.log('Not in this container - so close')
      }
    })

    bus.$off('load-more')
    bus.$off('close-advanced-form')
    bus.$off('reset-advanced-form')
    bus.$off('search-advanced-form')
    bus.$off('change-advanced-page')
    bus.$off('change-advanced-sort')
    bus.$off('before-leave-page')

    bus.$off('kunstner-saved')
  },
  mounted () {
    bus.$on('load-more', (type) => this.loadMore(type))
    bus.$on('close-advanced-form', () => this.closeAdvancedForm())
    bus.$on('reset-advanced-form', () => this.resetAdvancedForm())
    bus.$on('search-advanced-form', () => this.handleSearchSubmit())
    bus.$on('change-advanced-page', (page) => this.changeAdvancedPage(page))
    bus.$on('change-advanced-sort', (sortKey) => this.changeAdvancedSort(sortKey))
    bus.$on('before-leave-page', () => this.beforeLeave())
    bus.$on('hide-kunde-kontrakt-objekt-done', () => {
      if (this.$route.query.search) {
        this.showSearch()
      }
    })

    bus.$on('kunstner-saved', (data) => this.onKunstnerSaved(data))

    if (this.$route.query.advanced && this.$route.query.advanced === 'true') {
      // Go to the new search if object searching...
      const paramQuery = this.params.query ? this.params.query.trim() : null

      if (paramQuery) {
        sessionStorage.objectSearchQuery = paramQuery
      }

      this.$router.push({
        name: 'SearchObject'
      })
    } else {
      if (this.$route.query.search) {
        this.showSearch()
      }
    }
  },
  computed: {
    selectedSearchType: function () {
      return this.searchTypeOptions.find((i) => i.type === this.params.type)
    },
    searchMode: function () {
      // Normal search
      if (this.params.type === 'objekt') {
        return 'advanced'
      }

      return 'normal'
    },
    totalFound: function () {
      return this.result.objekt.total + this.result.kunde.total + this.result.kunstner.total + this.result.faktura.total + this.result.kontrakt.total
    },
    isSearching: function () {
      if (this.result.objekt.searching) {
        return true
      }

      if (this.result.kunde.searching) {
        return true
      }

      if (this.result.kunstner.searching) {
        return true
      }

      if (this.result.faktura.searching) {
        return true
      }

      if (this.result.kontrakt.searching) {
        return true
      }

      return false
    },
    isUpdating: function () {
      if (this.result.objekt.updating) {
        return true
      }

      if (this.result.kunde.updating) {
        return true
      }

      if (this.result.kunstner.updating) {
        return true
      }

      if (this.result.faktura.updating) {
        return true
      }

      if (this.result.kontrakt.updating) {
        return true
      }

      return false
    },
    showFilterView: function () {
      return this.searchMode !== 'advanced' && this.selectedSearchType.type === '' && !this.isSearching && this.totalFound > 0
    }
  },
  watch: {
    'userSettings.filter': function (newVal) {
      if (newVal !== undefined) {
        this.saveUserSettings()
      }
    },
    'userSettings.listtype': function (newVal, oldVal) {
      if (newVal !== undefined) {
        this.saveUserSettings()
      }
    }
  },
  methods: {
    onKunstnerSaved: function (data) {
      // Update the item in search result list, if found
      if (data.Idnr && this.result.kunstner && this.result.kunstner.items) {
        const found = this.result.kunstner.items.findIndex(i => i.Idnr === data.Idnr)

        if (found !== -1) {
          data.Navn = data.Navn.replace(/,+/, '').replace(/ +/, ' ')
          this.$set(this.result.kunstner.items, found, data)
        }
      }
    },
    onUpdateColumns: function (data) {
      this.userSettings.columns = this._.cloneDeep(data)

      this.saveUserSettings()
    },
    loadUserSettings: function () {
      try {
        this.userSettings = JSON.parse(localStorage.user_search_settings)
      } catch (e) {
        this.userSettings = {
          columns: [],
          listtype: 'small',
          filter: true
        }
      }

      if (!this.userSettings.columns || this.userSettings.columns.length < 1) {
        /**
         * Make default settings
         */
        this.userSettings.columns = []
        this.defaultColumns.forEach((item) => {
          this.userSettings.columns.push({ name: item.name, value: true })
        })
      } else {
        /**
         * Parse the saved settings
         */
        this.userSettings.columns = this.userSettings.columns.reduce((unique, item) => {
          // First we skip those that doesnt exist in the defalts array
          let found = this.defaultColumns.findIndex(i => i.name === item.name)

          if (found === -1) {
            return unique
          }

          // And check so it isn't already added
          found = unique.findIndex(i => i.name === item.name)

          if (found !== -1) {
            return unique
          }

          return [...unique, { name: item.name, value: item.value || false }]
        }, [])

        // And lastly append the rest of the values
        this.defaultColumns.forEach((defaultItem) => {
          let found = this.userSettings.columns.findIndex(i => i.name === defaultItem.name)

          if (found === -1) {
            this.userSettings.columns.push({ name: defaultItem.name, value: false })
          }
        })
      }
    },
    saveUserSettings: function () {
      localStorage.user_search_settings = JSON.stringify(this.userSettings)

      this.arrangeObjektData()
        .then(data => {
          this.result.objekt.items = data
        })
    },
    beforeLeave: function () {
      this.hideSearch()
    },
    toggleFilter: function (filterKey) {
      if (filterKey !== this.activeFilter) {
        this.activeFilter = filterKey
      }
    },
    showSearch: function () {
      this.$router.push({ query: { ...this.$route.query, search: true, advanced: this.params.type === 'objekt' } })
      this.searchIsActive = true

      window.jQuery('body').addClass('scroll-lock')
      window.jQuery(window).scrollTop(0)
    },
    hideSearch: function () {
      this.$router.push({ query: { ...this.$route.query, search: false, advanced: this.advanced } })
      this.searchIsActive = false
      this.searchIsActive = false

      window.jQuery('body').removeClass('scroll-lock')
    },
    clearSearch: function () {
      if (this.isSearching || this.isUpdating) {
        return
      }

      // Reset query string
      localStorage.searchQuery = ''
      this.defaultSearchParams.query = ''

      let type = this.params.type

      this.params = this._.clone(this.defaultSearchParams)
      this.params.type = type

      // Reset result
      this.resetResult()

      this.searchIsPerformed = false
    },
    toggleSearchTypePopover: function () {
      // If search not active and you try to show search type popover
      if (!this.searchTypePopoverActive && !this.searchIsActive) {
        this.showSearch()
      }

      this.searchTypePopoverActive = !this.searchTypePopoverActive
    },
    changeSearchType: function (typeItem) {
      let query = this.params.query

      if (typeItem.type === 'objekt') {
        // Go to new object search
        const vm = this

        const paramQuery = query ? query.trim() : null

        if (paramQuery) {
          sessionStorage.objectSearchQuery = paramQuery
        }

        this.$router.push({
          name: 'SearchObject'
        }, function () {
          vm.searchIsPerformed = false
          vm.searchTypePopoverActive = false
        })
      } else if (typeItem.type === 'kunde') {
        // Go to new customer search
        const vm = this

        const paramQuery = query ? query.trim() : null

        if (paramQuery) {
          sessionStorage.customerSearchQuery = paramQuery
        }

        this.$router.push({
          name: 'SearchCustomer'
        }, function () {
          vm.searchIsPerformed = false
          vm.searchTypePopoverActive = false
        })
      } else {
        if (this.params.type === 'objekt' && typeItem.type !== 'objekt') {
          this.resetResult()
        }

        this.params = this._.clone(this.defaultSearchParams)
        this.params.type = typeItem.type
        this.params.query = query

        this.searchIsPerformed = false
        this.searchTypePopoverActive = false

        if (this.params.query.trim() !== '') {
          this.handleSearchSubmit()
        }
      }
    },
    closeAdvancedForm: function () {
      // this.params.query = ''
      // this.changeSearchType({ type: '' })
      this.$router.push({ query: { ...this.$route.query, search: false, advanced: false } })
      this.hideSearch()
    },
    resetAdvancedForm: function () {
      let query = this.params.query

      this.params = this._.clone(this.defaultSearchParams)
      this.params.query = query
      this.params.type = 'objekt'
    },
    resetResult: function () {
      this.activeFilter = null

      this.result = {
        objekt: { page: 1, total: 0, pages: 0, objektData: [], items: [], searching: false, updating: false, sort: { sort: 'Objektnr', direction: 'desc' } },
        kunde: { page: 1, total: 0, pages: 0, items: [], searching: false, updating: false },
        kunstner: { page: 1, total: 0, pages: 0, items: [], searching: false, updating: false },
        faktura: { page: 1, total: 0, pages: 0, items: [], searching: false, updating: false },
        kontrakt: { page: 1, total: 0, pages: 0, items: [], searching: false, updating: false }
      }
      localStorage.searchResultNew = JSON.stringify(this.result)
    },
    loadMore: function (type) {
      let searchData = {
        query: this.params.query.trim()
      }

      if (type === 'objekt') {
        this.result.objekt.updating = true
        ++this.result.objekt.page

        this.fetchObjekt(searchData, true)
      } else if (type === 'kunde') {
        this.result.kunde.updating = true
        ++this.result.kunde.page

        this.fetchKunde(searchData, true)
      } else if (type === 'kunstner') {
        this.result.kunstner.updating = true
        ++this.result.kunstner.page

        this.fetchKunstner(searchData, true)
      } else if (type === 'faktura') {
        this.result.faktura.updating = true
        ++this.result.faktura.page

        this.fetchFaktura(searchData, true)
      } else if (type === 'kontrakt') {
        this.result.kontrakt.updating = true
        ++this.result.kontrakt.page

        this.fetchKontrakt(searchData, true)
      }
    },
    changeAdvancedPage: function (page) {
      if (page < 1) {
        page = 1
      } else if (page > this.result.objekt.pages) {
        page = this.result.objekt.pages
      }

      this.result.objekt.searching = true
      this.result.objekt.updating = true
      this.result.objekt.page = page

      let searchData = {
        query: this.params.query.trim(),
        _sort: this.result.objekt.sort
      }

      searchData = Object.assign({}, searchData, this.params)

      this.fetchObjekt(searchData, false)
    },
    changeAdvancedSort: function (sortKey) {
      let curr = this.result.objekt.sort.sort

      if (curr === sortKey) {
        this.result.objekt.sort.direction = this.result.objekt.sort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.result.objekt.sort.direction = 'desc'
      }

      this.result.objekt.sort.sort = sortKey

      this.changeAdvancedPage(1)
    },
    handleSearchSubmit: function () {
      if (this.isSearching || this.isUpdating) {
        // Skip if already searching...
        return
      }

      let isAdvanced = this.params.type === 'objekt'
      this.$router.push({ query: { ...this.$route.query, search: true, advanced: this.params.type === 'objekt' } })
      let searchData = {
        query: this.params.query.trim()
      }
      localStorage.searchQuery = this.params.query.trim()
      if (!isAdvanced) {
        // If normal search
        if (searchData.query === '') {
          return
        }
      }

      // Reset result
      this.resetResult()

      this.searchIsPerformed = true

      if (!isAdvanced) {
        // Normal search
        if (this.params.type === '') {
          this.result.objekt.searching = true
          this.result.kunde.searching = true
          this.result.kunstner.searching = true
          this.result.faktura.searching = true
          this.result.kontrakt.searching = true
        } else {
          this.result[this.params.type].searching = true
        }
      } else {
        // Advanced search
        this.result.objekt.searching = true

        if (searchData.query === '') {
          this.result.objekt.sort = { sort: 'Objektnr', direction: 'desc' }
        }

        searchData = Object.assign({}, searchData, this.params)
        searchData._sort = this.result.objekt.sort
      }

      if (this.result.objekt.searching) {
        this.fetchObjekt(searchData, false)
      }

      if (this.result.kunde.searching) {
        this.fetchKunde(searchData, false)
      }

      if (this.result.kunstner.searching) {
        this.fetchKunstner(searchData, false)
      }

      if (this.result.faktura.searching) {
        this.fetchFaktura(searchData, false)
      }

      if (this.result.kontrakt.searching) {
        this.fetchKontrakt(searchData, false)
      }
    },
    arrangeObjektData: function () {
      return new Promise(resolve => {
        const parsed = []
        const oarray = this.result.objekt.objektData || []

        oarray.forEach(obj => {
          const row = []

          let Minstepris = this.$options.filters.thousands(obj.Minstepris || 0)
          let Tilslag = '-'
          if (obj.Auksjon && this._.startsWith(obj.Auksjon, 'AR')) {
            Tilslag = 'Usolgt'
          } else if (obj.Salgspris > 0) {
            Tilslag = accounting.formatNumber(obj.Salgspris || 0, { precision: 0, thousand: ' ' })
          } else if (obj.Salgspris === '0' || obj.Salgspris === 0) {
            Tilslag = '0'
          }

          let VurderingLow = '-'
          if (obj.Vurderingspris2 > 0) {
            VurderingLow = accounting.formatNumber(obj.Vurderingspris2, { precision: 0, thousand: ' ' })
          } else if (obj.Vurderingspris2 === '0' || obj.Vurderingspris2 === 0) {
            VurderingLow = '0'
          }

          let VurderingHigh = '-'
          if (obj.Vurderingspris1 > 0) {
            VurderingHigh = accounting.formatNumber(obj.Vurderingspris1, { precision: 0, thousand: ' ' })
          } else if (obj.Vurderingspris1 === '0' || obj.Vurderingspris1 === 0) {
            VurderingHigh = '0'
          }

          let Title = ''
          if (obj.kunstner && obj.kunstner.Navn) {
            Title = obj.kunstner.Navn + (obj.Katalogtekst1 ? ' / ' + obj.Katalogtekst1 : '')
          } else if (obj.Kunstner) {
            Title = obj.Kunstner + (obj.Katalogtekst1 ? ' / ' + obj.Katalogtekst1 : '')
          } else if (obj.Katalogtekst1) {
            Title = obj.Katalogtekst1
          } else {
            Title = ''
          }

          this.userSettings.columns.forEach(c => {
            if (c.value && !(c.name === 'Bilde' && this.userSettings.listtype === 'none')) {
              switch (c.name) {
                case 'Bilde':
                  row.push({ key: c.name, val: obj.photos || null, cls: 'object-image-column unsortable' })
                  break
                case 'Objektnr':
                  row.push({ key: c.name, val: obj[c.name], cls: '' })
                  break
                case 'Auksjonsdato':
                  row.push({ key: c.name, val: this.$options.filters.avantidate(obj.auksjonsdato), cls: '' })
                  break
                case 'Kunstner':
                  row.push({ key: c.name, val: obj.kunstner ? obj.kunstner.Navn : '-', cls: '' })
                  break
                case 'Tittel':
                  row.push({ key: c.name, val: obj.Katalogtekst1 || '-', cls: '' })
                  break
                case 'Beskrivelse':
                  row.push({ key: c.name, val: obj.Objekt3 || '-', cls: '' })
                  break
                case 'Tekst':
                  row.push({ key: c.name, val: this.$options.filters.truncate(obj.Katalogtekst2 || '-', 400), cls: '', tooltip: true })
                  break
                case 'Tilslag':
                  row.push({ key: c.name, val: Tilslag, cls: 'align-right' })
                  break
                case 'Vurdering':
                  if (obj.Vurderingspris1 > 0 && obj.Vurderingspris2 > 0) {
                    row.push({ key: c.name, val: this.$options.filters.thousands(obj.Vurderingspris2) + ' - ' + this.$options.filters.thousands(obj.Vurderingspris1), cls: 'align-right' })
                  } else if (obj.Vurderingspris1 > 0) {
                    row.push({ key: c.name, val: this.$options.filters.thousands(obj.Vurderingspris1), cls: 'align-right' })
                  } else if (obj.Vurderingspris2 > 0) {
                    row.push({ key: c.name, val: this.$options.filters.thousands(obj.Vurderingspris2), cls: 'align-right' })
                  } else {
                    row.push({ key: c.name, val: '-' })
                  }
                  break
                case 'Minstepris':
                  row.push({ key: c.name, val: Minstepris, cls: 'align-right' })
                  break
                case 'Signert':
                  row.push({ key: c.name, val: obj.Objekt1 || '-' })
                  break
                case 'Lokasjon':
                  row.push({ key: c.name, val: obj.lokasjon ? obj.lokasjon.Lokasjonkode : '-' })
                  break
                case 'Hentet':
                  row.push({ key: c.name, val: this.$options.filters.avantidate(obj.Lev_dato), cls: '' })
                  break
                case 'Avdeling':
                  row.push({ key: c.name, val: obj.avdeling ? obj.avdeling.Beskrivelse : '-' })
                  break
                case 'Type':
                  row.push({ key: c.name, val: obj.Objekttype || '-', cls: '' })
                  break
                case 'Tekst_ok':
                  row.push({ key: c.name, val: obj.Tekst_ok === 'T', cls: '', bool: true })
                  break
                case 'Fototatt':
                  row.push({ key: c.name, val: obj.Fototatt === 'T', cls: '', bool: true })
                  break
                case 'Registrert dato':
                  row.push({ key: c.name, val: this.$options.filters.avantidate(obj.Registrert_dato), cls: '' })
                  break
                case 'Relevans':
                  row.push({ key: c.name, val: obj._score || '-', cls: '' })
                  break
                case 'Bet_dato':
                  row.push({ key: c.name, val: this.$options.filters.avantidate(obj.Bet_dato), cls: '' })
                  break
                case 'Lev_dato':
                  row.push({ key: c.name, val: this.$options.filters.avantidate(obj.Lev_dato), cls: '' })
                  break
                case 'Fakturert':
                  row.push({ key: c.name, val: obj.Fakturert === 'F', cls: '', bool: true })
                  break
                case 'oppgjor_dato':
                  row.push({ key: c.name, val: this.$options.filters.avantidate(obj.oppgjor_dato), cls: '' })
                  break
                case 'oppgjor_belop':
                  row.push({ key: c.name, val: this.$options.filters.thousands(obj.oppgjor_belop), cls: 'align-right' })
                  break
                case 'Katalognr':
                  row.push({ key: c.name, val: obj[c.name] || '-', cls: '' })
                  break
                case 'Kontraktansvarlig':
                  row.push({ key: c.name, val: obj.kontraktsansvarlig, cls: '' })
                  break
                default:
                  break
              }
            }
          })

          parsed.push({
            Objektid: obj.Objektid,
            Objektnr: obj.Objektnr,
            Kundenr: obj.kontrakt.Kundenr,
            Buyer: obj.mottaker && obj.mottaker.Kundenr ? obj.mottaker : null,
            Seller: obj.innlevav && obj.innlevav.Kundenr ? obj.innlevav : null,
            Kontraktnr: obj.Kontraktnr,
            Kunstner: obj.kunstner ? obj.kunstner : null,
            Minstepris: Minstepris,
            VurderingLow: VurderingLow,
            VurderingHigh: VurderingHigh,
            Tilslag: Tilslag,
            Title: Title,
            Antall: obj.Antall,
            cols: row
          })
        })

        resolve(parsed)
      })
    },
    fetchObjekt: function (searchData, append) {
      // Fix dates
      if (searchData.date_from) {
        const df = new Date(Date.parse(searchData.date_from))
        searchData.date_from = moment(df).format('YYYY-MM-DD')
      }

      if (searchData.date_to) {
        const dt = new Date(Date.parse(searchData.date_to))
        searchData.date_to = moment(dt).format('YYYY-MM-DD')
      }

      SearchService.objekt(searchData, this.result.objekt.page)
        .then((response) => {
          if (!append) {
            this.result.objekt.objektData = response.data

            this.arrangeObjektData()
              .then(data => {
                this.result.objekt.items = data
                this.result.objekt.total = response.headers['x-paging-count'] ? parseInt(response.headers['x-paging-count']) : 0
                this.result.objekt.pages = response.headers['x-paging-pagecount'] ? parseInt(response.headers['x-paging-pagecount']) : 1
              })
          } else {
            let newItems = response.data || []
            newItems.forEach(item => {
              this.result.objekt.objektData.push(item)
            })
          }
        })
        .catch((error) => {
          console.log('Error')
          console.log(error)
        })
        .then(() => {
          this.result.objekt.updating = false
          this.result.objekt.searching = false

          this.exportParams = this._.cloneDeep(searchData)

          localStorage.searchExport = JSON.stringify(searchData)
          localStorage.searchResultNew = JSON.stringify(this.result)
        })
    },
    fetchKunde: function (searchData, append) {
      SearchService.kunde(searchData, this.result.kunde.page)
        .then((response) => {
          if (!append) {
            this.result.kunde.items = response.data
          } else {
            let newItems = response.data || []
            newItems.forEach(item => {
              this.result.kunde.items.push(item)
            })
          }

          this.result.kunde.total = response.headers['x-paging-count'] ? parseInt(response.headers['x-paging-count']) : 0
          this.result.kunde.pages = response.headers['x-paging-pagecount'] ? parseInt(response.headers['x-paging-pagecount']) : 1
        })
        .catch(() => {
          console.log('Error')
        })
        .then(() => {
          this.result.kunde.updating = false
          this.result.kunde.searching = false
          localStorage.searchResultNew = JSON.stringify(this.result)
        })
    },
    fetchKunstner: function (searchData, append) {
      SearchService.kunstner(searchData, this.result.kunstner.page)
        .then((response) => {
          if (!append) {
            this.result.kunstner.items = response.data
          } else {
            let newItems = response.data || []
            newItems.forEach(item => {
              this.result.kunstner.items.push(item)
            })
          }

          this.result.kunstner.total = response.headers['x-paging-count'] ? parseInt(response.headers['x-paging-count']) : 0
          this.result.kunstner.pages = response.headers['x-paging-pagecount'] ? parseInt(response.headers['x-paging-pagecount']) : 1
        })
        .catch(() => {
          console.log('Error')
        })
        .then(() => {
          this.result.kunstner.updating = false
          this.result.kunstner.searching = false
          localStorage.searchResultNew = JSON.stringify(this.result)
        })
    },
    fetchFaktura: function (searchData, append) {
      SearchService.faktura(searchData, this.result.faktura.page)
        .then((response) => {
          if (!append) {
            this.result.faktura.items = response.data
          } else {
            let newItems = response.data || []
            newItems.forEach(item => {
              this.result.faktura.items.push(item)
            })
          }

          this.result.faktura.total = response.headers['x-paging-count'] ? parseInt(response.headers['x-paging-count']) : 0
          this.result.faktura.pages = response.headers['x-paging-pagecount'] ? parseInt(response.headers['x-paging-pagecount']) : 1
        })
        .catch(() => {
          console.log('Error')
        })
        .then(() => {
          this.result.faktura.updating = false
          this.result.faktura.searching = false
          localStorage.searchResultNew = JSON.stringify(this.result)
        })
    },

    fetchKontrakt: function (searchData, append) {
      SearchService.kontrakt(searchData, this.result.kontrakt.page)
        .then((response) => {
          if (!append) {
            this.result.kontrakt.items = response.data
          } else {
            let newItems = response.data || []
            newItems.forEach(item => {
              this.result.kontrakt.items.push(item)
            })
          }

          this.result.kontrakt.total = response.headers['x-paging-count'] ? parseInt(response.headers['x-paging-count']) : 0
          this.result.kontrakt.pages = response.headers['x-paging-pagecount'] ? parseInt(response.headers['x-paging-pagecount']) : 1
        })
        .catch(() => {
          console.log('Error')
        })
        .then(() => {
          this.result.kontrakt.updating = false
          this.result.kontrakt.searching = false
          localStorage.searchResultNew = JSON.stringify(this.result)
        })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Admin/TheMainSearch.vue